import {
  ReportSection,
  ReportSectionConfigurationBase,
  ReportSectionConfigurationControlType,
  ReportSectionDataSection,
  ReportType,
  ReportViewModelSection
} from '@capital-access/reporting/common';
import { PeerOwnershipInstitutionsGlobalViewModel } from './peer-ownership-institutions.model';

export class PeerOwnershipProspectiveShareholderInstitutionsGlobalConfiguration extends ReportSectionConfigurationBase {
  typeName = 'PeerOwnershipProspectiveShareholderInstitutionsGlobalConfiguration';
  nodataLocalizationKeyName = 'peerOwnershipProspectiveShareholderInstitutionsGlobal';
  readonly reportSection = ReportSection.PeerOwnershipProspectiveShareholderInstitutionsGlobal;
  readonly name = 'peerOwnershipProspectiveShareholderInstitutionsGlobal';
  readonly displayName = 'Prospective Shareholder Institutions - Global';
  readonly applicableReportTypes = [ReportType.PeerOwnershipReport];
  readonly reportViewModelSections = [ReportViewModelSection.PeerOwnershipReport_InstitutionsGlobal];
  minSize = 4;
  maxSize = 4;
  defaultSize = 4;
  override maxGridUnitsAllowed = 13;

  calculateTotalSelectedUnits(): number {
    return this.availableDataSections
      .filter(section => section.isSelected)
      .reduce((sum, section) => sum + (section.unit || 0), 0);
  }

  isMaxUnitsLimitReached(): boolean {
    return this.calculateTotalSelectedUnits() >= this.maxGridUnitsAllowed;
  }

  shouldDisableDataSection(section: { unit?: number; isSelected?: boolean }, sumWeight: number): boolean {
    if (!section.unit) {
      return false;
    }

    if (section.isSelected) {
      return false;
    }

    return sumWeight + section.unit > this.maxGridUnitsAllowed;
  }

  getDisabledSectionsMap(): Map<string, boolean> {
    const disabledMap = new Map<string, boolean>();
    const currentWeight = this.calculateTotalSelectedUnits();

    this.availableDataSections.forEach(section => {
      disabledMap.set(section.name, this.shouldDisableDataSection(section, currentWeight));
    });

    return disabledMap;
  }

  readonly availableDataSections: ReportSectionDataSection[] = [
    {
      name: 'institutionsInstitution',
      displayName: 'Institution',
      type: ReportSectionConfigurationControlType.Text,
      isSelected: true,
      canConfigure: false,
      isStatic: true,
      unit: 2.5,
      groupName: 'column'
    },
    {
      name: 'institutionsEquityAssets',
      displayName: 'Equity Assets',
      type: ReportSectionConfigurationControlType.Decimal,
      isSelected: true,
      canConfigure: true,
      unit: 0.8,
      groupName: 'column'
    },
    {
      name: 'institutionsDrEquityAssets',
      displayName: 'DR Equity Assets',
      type: ReportSectionConfigurationControlType.Decimal,
      isSelected: true,
      canConfigure: true,
      unit: 0.8,
      groupName: 'column'
    },
    {
      name: 'institutionsPurchPower',
      displayName: 'Purch. Power',
      type: ReportSectionConfigurationControlType.Decimal,
      isSelected: true,
      canConfigure: true,
      unit: 0.5,
      groupName: 'column'
    },
    {
      name: 'institutionsSuitScore',
      displayName: 'Suit. Score',
      type: ReportSectionConfigurationControlType.Number,
      isSelected: true,
      canConfigure: true,
      unit: 0.5,
      groupName: 'column'
    },
    {
      name: 'institutionsPeerValue',
      displayName: 'Peer Value',
      type: ReportSectionConfigurationControlType.Decimal,
      isSelected: true,
      canConfigure: true,
      unit: 0.5,
      groupName: 'column'
    },
    {
      name: 'institutionsPeerValueChange',
      displayName: 'Peer Value Change',
      type: ReportSectionConfigurationControlType.Decimal,
      isSelected: true,
      canConfigure: true,
      unit: 0.9,
      groupName: 'column'
    },
    {
      name: 'institutionsPeersHeld',
      displayName: '# Peers held',
      type: ReportSectionConfigurationControlType.Number,
      isSelected: true,
      canConfigure: true,
      unit: 0.5,
      groupName: 'column'
    },
    {
      name: 'institutionsSectorValue',
      displayName: 'Sector Value',
      type: ReportSectionConfigurationControlType.Decimal,
      isSelected: true,
      canConfigure: true,
      unit: 0.5,
      groupName: 'column'
    },
    {
      name: 'institutionsSectorValueChange',
      displayName: 'Sctr. Value Change',
      type: ReportSectionConfigurationControlType.Decimal,
      isSelected: true,
      canConfigure: true,
      unit: 0.9,
      groupName: 'column'
    },
    {
      name: 'institutionsSectorPctPort',
      displayName: 'Sector % Portfolio',
      type: ReportSectionConfigurationControlType.Number,
      isSelected: true,
      canConfigure: true,
      unit: 0.5,
      groupName: 'column'
    },
    {
      name: 'institutionsSectorSecuritiesHeld',
      displayName: '# sector Securities held',
      type: ReportSectionConfigurationControlType.Number,
      isSelected: true,
      canConfigure: true,
      unit: 0.9,
      groupName: 'column'
    },
    {
      name: 'institutionsRegionValue',
      displayName: 'Region Value',
      type: ReportSectionConfigurationControlType.Decimal,
      isSelected: true,
      canConfigure: true,
      unit: 0.5,
      groupName: 'column'
    },
    {
      name: 'institutionsRegionValueChange',
      displayName: 'Rgn. Value Change',
      type: ReportSectionConfigurationControlType.Decimal,
      isSelected: true,
      canConfigure: true,
      unit: 0.8,
      groupName: 'column'
    },
    {
      name: 'institutionsRegionPctPort',
      displayName: 'Region % Portfolio',
      type: ReportSectionConfigurationControlType.Number,
      isSelected: true,
      canConfigure: true,
      unit: 0.5,
      groupName: 'column'
    },
    {
      name: 'institutionsStyle',
      displayName: 'Style',
      type: ReportSectionConfigurationControlType.Text,
      isSelected: true,
      canConfigure: true,
      unit: 0.8,
      groupName: 'column'
    },
    {
      name: 'institutionsCity',
      displayName: 'City',
      type: ReportSectionConfigurationControlType.Text,
      isSelected: true,
      canConfigure: true,
      unit: 1.1,
      groupName: 'column'
    }
  ];
}

export interface PeerOwnershipProspectiveShareholderInstitutionsGlobalDataBag {
  [ReportViewModelSection.PeerOwnershipReport_InstitutionsGlobal]: PeerOwnershipInstitutionsGlobalViewModel;
}
